import { useState } from 'react';
import { X, Send, Mail, Crown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminInvitesModal({ onClose }) {
  const { session } = useAuth();
  const [emails, setEmails] = useState('');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const sendInvites = async () => {
    // Parse emails (comma or newline separated)
    const emailList = emails
      .split(/[\n,]+/)
      .map(e => e.trim())
      .filter(e => e.length > 0 && e.includes('@'));

    if (emailList.length === 0) {
      setError('Please enter at least one valid email address');
      return;
    }

    try {
      setSending(true);
      setError('');
      setResults(null);

      const response = await fetch(`${API_URL}/api/invite/send-invites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`
        },
        body: JSON.stringify({ emails: emailList })
      });

      if (!response.ok) {
        throw new Error('Failed to send invites');
      }

      const data = await response.json();
      setResults(data);
      
      // Clear form on success
      if (data.success) {
        setEmails('');
      }
    } catch (err) {
      console.error('Error sending invites:', err);
      setError(err.message || 'Failed to send invites');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto border-4 border-orange-500/50 shadow-2xl"
        style={{
          boxShadow: '0 0 60px rgba(255, 107, 53, 0.5)'
        }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-orange-600 to-red-600 p-6 flex items-center justify-between border-b-4 border-orange-500">
          <div className="flex items-center gap-3">
            <Mail className="w-8 h-8 text-white" />
            <div>
              <h2 className="text-2xl font-bold text-white font-mono tracking-wide">
                ☢️ Send Beta Invites
              </h2>
              <p className="text-orange-100 text-sm mt-1">Invite friends & family with auto-VIP access</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Info Box */}
          <div className="bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 border-2 border-yellow-500 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Crown className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-yellow-400 font-bold text-lg mb-2">VIP Access Invites</h3>
                <p className="text-yellow-100 text-sm leading-relaxed">
                  Each invite includes:
                </p>
                <ul className="text-yellow-100 text-sm mt-2 space-y-1 list-disc list-inside">
                  <li>Gorgeous branded email with GTNW3 Characters badge</li>
                  <li>Unique invite link to /invite page</li>
                  <li>Automatic VIP status (unlimited games, no ads)</li>
                  <li>Personal message from you (Kaj)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Email Input */}
          <div>
            <label className="block text-slate-300 font-semibold mb-2 text-lg">
              <Mail className="inline w-5 h-5 mr-2" />
              Email Addresses
            </label>
            <p className="text-slate-400 text-sm mb-3">
              Enter one email per line, or separate with commas
            </p>
            <textarea
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder="friend1@example.com&#10;friend2@example.com&#10;family@example.com"
              className="w-full h-48 bg-slate-800 border-2 border-slate-600 rounded-lg p-4 text-white font-mono text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 resize-none"
              disabled={sending}
            />
            <p className="text-slate-500 text-xs mt-2">
              {emails.split(/[\n,]+/).filter(e => e.trim().includes('@')).length} valid email(s) detected
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-500/20 border-2 border-red-500 text-red-200 p-4 rounded-lg">
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Results Display */}
          {results && (
            <div className={`border-2 rounded-lg p-4 ${
              results.success 
                ? 'bg-green-500/20 border-green-500 text-green-200' 
                : 'bg-red-500/20 border-red-500 text-red-200'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-lg">
                  {results.success ? '✅ Invites Sent!' : '❌ Some Invites Failed'}
                </h3>
                <span className="text-sm">
                  {results.total_sent} / {results.total_requested} sent
                </span>
              </div>
              
              {/* Individual Results */}
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {results.results?.map((result, index) => (
                  <div 
                    key={index}
                    className={`text-sm p-2 rounded ${
                      result.success 
                        ? 'bg-green-500/10' 
                        : 'bg-red-500/10'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono">{result.email}</span>
                      <span>{result.success ? '✅' : '❌'}</span>
                    </div>
                    {result.success && result.invite_code && (
                      <p className="text-xs opacity-70 mt-1">
                        Code: {result.invite_code}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Send Button */}
          <button
            onClick={sendInvites}
            disabled={sending || !emails.trim()}
            className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white font-bold py-4 px-6 rounded-lg hover:from-orange-700 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-lg uppercase tracking-wide flex items-center justify-center gap-3"
            style={{
              boxShadow: '0 6px 25px rgba(255, 107, 53, 0.5)'
            }}
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                Sending Invites...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                ☢️ Send Invites ☢️
              </>
            )}
          </button>

          {/* Preview Info */}
          <div className="bg-slate-800/50 border border-slate-600 rounded-lg p-4">
            <h4 className="text-slate-300 font-semibold mb-2 text-sm">📧 Email Preview:</h4>
            <ul className="text-slate-400 text-xs space-y-1">
              <li>• Subject: "☢️ YOU'RE INVITED to GTNW3 Beta | VIP Access Granted 🎖️"</li>
              <li>• From: commander@gtnw3.games</li>
              <li>• Features: GTNW3 Characters badge, device recommendations, invite link</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
